import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError, useApi } from "@/lib/api";
import { AdminTitle, AInput, ATextarea, ASelect, linesToList, commaToList } from "./AdminUi";

const EMPTY = { slug: "", hub: "direction", category: "", title: "", summary: "", intro: "", body: "", considerations: "", next_steps: "", related: "", services: "", status: "draft", review_status: "pending_expert_review", reviewed_at: "", content_owner: "", next_review: "", safeguarding_notes: "" };

const toForm = (a) => ({ ...a, considerations: a.considerations.join("\n"), next_steps: a.next_steps.join("\n"), related: a.related.join(", "), services: a.services.join(", "), reviewed_at: a.reviewed_at || "", content_owner: a.content_owner || "", next_review: a.next_review || "", safeguarding_notes: a.safeguarding_notes || "" });

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [extra, setExtra] = useState({ sources: [], addiction_types: ["alcohol", "drugs"] });
  const [busy, setBusy] = useState(false);
  const { data: cats } = useApi(`/categories?hub=${f.hub}`);
  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/articles/${id}`).then(({ data }) => {
      setF(toForm(data));
      setExtra({ sources: data.sources || [], addiction_types: data.addiction_types || [] });
    }).catch((e) => toast.error(formatError(e)));
  }, [id, isNew]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      slug: f.slug, hub: f.hub, category: f.category, title: f.title, summary: f.summary, intro: f.intro, body: f.body,
      considerations: linesToList(f.considerations), next_steps: linesToList(f.next_steps), related: commaToList(f.related), services: commaToList(f.services),
      status: f.status, review_status: f.review_status, reviewed_at: f.reviewed_at || null, content_owner: f.content_owner, next_review: f.next_review || null, safeguarding_notes: f.safeguarding_notes, ...extra,
    };
    try {
      if (isNew) await api.post("/admin/articles", payload);
      else await api.put(`/admin/articles/${id}`, payload);
      toast.success("Article saved");
      navigate("/admin/articles");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="max-w-4xl space-y-5" data-testid="article-editor">
      <AdminTitle title={isNew ? "New article" : "Edit article"}>Body formatting: start a line with “## ” for a heading and “- ” for a bullet point. Leave a blank line between paragraphs.</AdminTitle>
      <div className="grid gap-5 sm:grid-cols-2">
        <AInput id="art-title" label="Title" value={f.title} onChange={set("title")} required />
        <AInput id="art-slug" label="Web address (slug)" hint="Lowercase letters, numbers and hyphens" value={f.slug} onChange={set("slug")} required pattern="[a-z0-9-]+" />
        <ASelect id="art-hub" label="Hub" value={f.hub} onChange={set("hub")} options={[["direction", "Direction"], ["understanding", "Understanding"]]} />
        <ASelect id="art-category" label="Category" value={f.category} onChange={set("category")} options={[["", "Choose a category"], ...(cats || []).map((c) => [c.slug, c.name])]} />
      </div>
      <ATextarea id="art-summary" label="Summary" value={f.summary} onChange={set("summary")} rows={2} />
      <ATextarea id="art-intro" label="Introduction" value={f.intro} onChange={set("intro")} rows={3} />
      <ATextarea id="art-body" label="Body" value={f.body} onChange={set("body")} rows={14} />
      <ATextarea id="art-considerations" label="Key considerations (one per line)" value={f.considerations} onChange={set("considerations")} />
      <ATextarea id="art-next" label="Suggested next steps (one per line)" value={f.next_steps} onChange={set("next_steps")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <AInput id="art-related" label="Related article slugs (comma separated)" value={f.related} onChange={set("related")} />
        <AInput id="art-services" label="Directory entry slugs (comma separated)" value={f.services} onChange={set("services")} />
        <ASelect id="art-status" label="Status" value={f.status} onChange={set("status")} options={[["draft", "Draft (hidden)"], ["published", "Published"]]} />
        <ASelect id="art-review" label="Expert review" value={f.review_status} onChange={set("review_status")} options={[["pending_expert_review", "Pending expert review"], ["expert_reviewed", "Expert reviewed"]]} />
        <AInput id="art-reviewed-at" label="Review date" type="date" value={f.reviewed_at} onChange={set("reviewed_at")} />
        <AInput id="art-next-review" label="Next review date" type="date" value={f.next_review} onChange={set("next_review")} />
        <AInput id="art-owner" label="Content owner" value={f.content_owner} onChange={set("content_owner")} />
      </div>
      <ATextarea id="art-safeguarding" label="Safeguarding considerations (internal)" value={f.safeguarding_notes} onChange={set("safeguarding_notes")} rows={2} />
      <div className="flex gap-3">
        <button type="submit" className="btn-primary" disabled={busy || !f.category} data-testid="article-save-button">{busy ? "Saving…" : "Save article"}</button>
        <button type="button" className="btn-secondary" onClick={() => navigate("/admin/articles")} data-testid="article-cancel-button">Cancel</button>
      </div>
    </form>
  );
}
